import { useQuery, useQueries } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Link2,
  BarChart3,
  MousePointerClick,
  TrendingUp,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { linksListOptions } from "@/features/links";
import { analyticsOptions } from "@/features/analytics";
import type { AnalyticsResponse, ClicksByDay, TopEntry } from "@/features/analytics";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { StatCard } from "@/components/ui/stat-card";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";

function aggregateAnalytics(results: AnalyticsResponse[]): {
  totalClicks: number;
  uniqueClicks: number;
  clicksByDay: ClicksByDay[];
  topCountries: TopEntry[];
} {
  let totalClicks = 0;
  let uniqueClicks = 0;
  const dayMap = new Map<string, { total: number; unique: number }>();
  const countryMap = new Map<string, number>();

  for (const r of results) {
    totalClicks += r.totalClicks;
    uniqueClicks += r.uniqueClicks;

    for (const d of r.clicksByDay) {
      const existing = dayMap.get(d.date) ?? { total: 0, unique: 0 };
      dayMap.set(d.date, {
        total: existing.total + d.totalClicks,
        unique: existing.unique + d.uniqueClicks,
      });
    }

    for (const c of r.topCountries) {
      countryMap.set(c.name, (countryMap.get(c.name) ?? 0) + c.count);
    }
  }

  const clicksByDay = Array.from(dayMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, counts]) => ({
      date,
      totalClicks: counts.total,
      uniqueClicks: counts.unique,
    }));

  const topCountries = Array.from(countryMap.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  return { totalClicks, uniqueClicks, clicksByDay, topCountries };
}

export function DashboardPage() {
  const { data: linksData, isLoading } = useQuery(linksListOptions({ pageSize: 5 }));

  // Build date range for last 30 days
  const to = new Date().toISOString().split("T")[0]!;
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - 29);
  const from = fromDate.toISOString().split("T")[0]!;

  // Fetch analytics for each recent link
  const shortCodes = linksData?.links.map((l) => l.shortCode) ?? [];
  const analyticsQueries = useQueries({
    queries: shortCodes.map((code) => analyticsOptions(code, { from, to })),
  });

  const analyticsResults = analyticsQueries
    .filter((q) => q.isSuccess && q.data)
    .map((q) => q.data!);

  const aggregated = aggregateAnalytics(analyticsResults);
  const analyticsLoading = analyticsQueries.some((q) => q.isLoading);

  const summaryStats = {
    totalLinks: linksData?.pagination.page === 1 ? (linksData?.links.length ?? 0) : 0,
    activeLinks: linksData?.links.filter((l) => l.status === "Active").length ?? 0,
    totalClicks: aggregated.totalClicks,
    uniqueClicks: aggregated.uniqueClicks,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400" />
      </div>
    );
  }

  const hasAnalyticsData = aggregated.clicksByDay.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Overview of your link management activity</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total Links" value={summaryStats.totalLinks} icon={Link2} />
        <StatCard title="Active Links" value={summaryStats.activeLinks} icon={TrendingUp} />
        <StatCard title="Total Clicks" value={summaryStats.totalClicks} icon={MousePointerClick} />
        <StatCard title="Unique Clicks" value={summaryStats.uniqueClicks} icon={BarChart3} />
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Clicks Over Time (30 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <div className="flex h-[300px] items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400" />
              </div>
            ) : hasAnalyticsData ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={aggregated.clicksByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="totalClicks" stroke="#2563eb" strokeWidth={2} name="Total" />
                  <Line type="monotone" dataKey="uniqueClicks" stroke="#16a34a" strokeWidth={2} name="Unique" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
                No click data yet. Analytics will appear here once links receive clicks.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Countries</CardTitle>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <div className="flex h-[300px] items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400" />
              </div>
            ) : aggregated.topCountries.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={aggregated.topCountries} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
                No country data yet. Geographic stats will appear here once links receive clicks.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Links */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Links</CardTitle>
          <Link
            to="/links"
            className="text-sm text-blue-600 hover:underline dark:text-blue-400"
          >
            View all
          </Link>
        </CardHeader>
        <CardContent>
          {linksData?.links.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No links created yet.{" "}
              <Link to="/create" className="text-blue-600 hover:underline dark:text-blue-400">
                Create your first link
              </Link>
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 dark:text-gray-400">
                    <th className="pb-3 font-medium">Short Code</th>
                    <th className="pb-3 font-medium">Destination</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {linksData?.links.map((link) => (
                    <tr key={link.shortCode} className="border-b last:border-0">
                      <td className="py-3">
                        <Link
                          to="/links/$shortCode"
                          params={{ shortCode: link.shortCode }}
                          className="font-mono text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {link.shortCode}
                        </Link>
                      </td>
                      <td className="max-w-xs truncate py-3 text-gray-600 dark:text-gray-400">{link.longUrl}</td>
                      <td className="py-3">
                        <Badge variant={link.linkType === "General" ? "default" : "secondary"}>
                          {link.linkType}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <Badge variant={link.status === "Active" ? "success" : "warning"}>
                          {link.status}
                        </Badge>
                      </td>
                      <td className="py-3 text-gray-500 dark:text-gray-400">{formatDate(link.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
